import { useMemo } from "react";
import type { TypedDocumentNode } from "@apollo/client";
import { useQuery } from "@apollo/client/react";

import type {
  AccessSummaryMember,
  TeamMembersSource,
} from "./use-object-sharing";

type TeamMembersVariables = {
  issueId?: string;
  workflowId?: string;
  teamId: string;
};

// One team's roster resolved against the shared subject — the rows the
// share dialog lists under an expanded team. Runs only while the team is
// expanded. Every share mutation in `useObjectSharing` refetches this
// operation by name, so a level changed on any row refreshes every open
// roster without the two hooks knowing about each other's variables.
export function useTeamAccessMembers(
  source: TeamMembersSource,
  teamId: string,
  expanded: boolean,
) {
  const document = source.document as TypedDocumentNode<
    unknown,
    TeamMembersVariables
  >;
  const variables = useMemo(
    (): TeamMembersVariables =>
      source.idKey === "issueId"
        ? { issueId: source.id ?? "", teamId }
        : { workflowId: source.id ?? "", teamId },
    [source.idKey, source.id, teamId],
  );
  const { data, loading } = useQuery(document, {
    variables,
    skip: !expanded || !source.id,
  });
  const members: AccessSummaryMember[] = useMemo(
    () => source.selectMembers(data),
    [source, data],
  );
  return { members, loading: loading && members.length === 0 };
}
